import { Breadcrumb } from "@/components/breadcrumb";
import { PageHeader } from "@/components/page-header";
import { DocButton } from "@/components/ui/doc-button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useTabParam } from "@/hooks/use-tab-param";
import {
  useAllCustomFields,
  useClientTabs,
  useClientViews,
  useDisplayDates,
  useSupportedLanguages,
  useTenantUsers,
} from "@/lib/hooks/use-clients";
import { UserLanguageProvider } from "@/lib/i18n";
import { useT } from "@/lib/i18n/use-t";
import { CustomFieldsWrapper } from "../custom-fields-wrapper";
import { ClientCostCategoriesWrapper } from "./client-cost-categories-wrapper";
import { ClientDisplaySettingsWrapper } from "./client-display-settings-wrapper";
import { ClientTabsWrapper } from "./client-tabs-wrapper";
import { ClientViewsWrapper } from "./client-views-wrapper";
import { T } from "./clients-settings-wrapper.i18n";

const TAB_VALUES = ["fields", "tabs", "views", "cost-categories", "settings"] as const;

function ClientsSettingsContent() {
  const t = useT(T);
  const [activeTab, setActiveTab] = useTabParam(TAB_VALUES, "fields");

  const { customFields, isLoading: fieldsLoading } = useAllCustomFields();
  const { clientTabs, isLoading: tabsLoading } = useClientTabs();
  const { isLoading: viewsLoading } = useClientViews();
  const { tenantUsers } = useTenantUsers();
  const { displayDates, isLoading: datesLoading } = useDisplayDates();
  const { supportedLanguages } = useSupportedLanguages();

  const isLoading = fieldsLoading || tabsLoading || viewsLoading || datesLoading;

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Breadcrumb items={[{ label: t.settings, href: "/settings" }, { label: t.clients }]} />
        <div className="flex items-center justify-center py-12 text-muted-foreground">
          {t.loading}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        breadcrumbs={[{ label: t.settings, href: "/settings" }, { label: t.clients }]}
        title={t.clients}
        description={t.pageDescription}
        actions={
          <DocButton docSlug="client-settings" variant="outline" size="sm" className="gap-2">
            {t.documentation}
          </DocButton>
        }
      />

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList>
          <TabsTrigger value="fields">{t.clientFields}</TabsTrigger>
          <TabsTrigger value="tabs">{t.clientTabs}</TabsTrigger>
          <TabsTrigger value="views">{t.clientViews}</TabsTrigger>
          <TabsTrigger value="cost-categories">{t.costCategories}</TabsTrigger>
          <TabsTrigger value="settings">{t.tabSettings}</TabsTrigger>
        </TabsList>

        <TabsContent value="fields" className="mt-6">
          <CustomFieldsWrapper
            initialFields={customFields}
            initialTabs={clientTabs}
            supportedLanguages={supportedLanguages}
          />
        </TabsContent>

        <TabsContent value="tabs" className="mt-6">
          <ClientTabsWrapper initialFields={customFields} supportedLanguages={supportedLanguages} />
        </TabsContent>

        <TabsContent value="views" className="mt-6">
          <ClientViewsWrapper tenantUsers={tenantUsers} supportedLanguages={supportedLanguages} />
        </TabsContent>

        <TabsContent value="cost-categories" className="mt-6">
          <ClientCostCategoriesWrapper supportedLanguages={supportedLanguages} />
        </TabsContent>

        <TabsContent value="settings" className="mt-6">
          <ClientDisplaySettingsWrapper initialDisplayDates={displayDates} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

export function ClientsSettingsWrapper() {
  const { supportedLanguages } = useSupportedLanguages();
  // First language in supportedLanguages is the tenant default
  const tenantDefaultLanguage = supportedLanguages[0] || "en_GB";

  return (
    <UserLanguageProvider userLanguage="en_GB" tenantDefaultLanguage={tenantDefaultLanguage}>
      <ClientsSettingsContent />
    </UserLanguageProvider>
  );
}
